import { useMemo } from 'react';

import { useFishGroupsController_getEvents } from '@/services/hooks';
import type {
    FishGroupsController_getEventsQueryParams,
    FishGroupTransferEventDto,
} from '@/services/types';

import type { DomainQueryOptions } from '../shared/types';

import type { UseGetFishGroupEventsResult } from './historyTypes';
import { mapFishGroupEventsToDomain } from './mappers';
import type { FishGroupEventType } from './types';

export interface UseGetFishGroupEventsFilters {
    eventType?: FishGroupEventType;
}

export const useGetFishGroupEvents = (
    clientId: number,
    fishGroupId: number,
    filters?: UseGetFishGroupEventsFilters,
    options?: DomainQueryOptions<FishGroupTransferEventDto[]>
): UseGetFishGroupEventsResult => {
    // The generated query params still predate `delousing` — they are rebuilt
    // from a running backend by `yarn updateApi`, which has not happened yet.
    // The domain owns the authoritative list, so the cast is confined to this
    // boundary; drop it once the spec is regenerated.
    const queryParams: FishGroupsController_getEventsQueryParams | undefined = filters?.eventType
        ? ({ eventType: filters.eventType } as FishGroupsController_getEventsQueryParams)
        : undefined;
    const query = useFishGroupsController_getEvents(clientId, fishGroupId, queryParams, options);

    const events = useMemo(() => {
        if (!query.data?.data) return [];
        return mapFishGroupEventsToDomain(query.data.data);
    }, [query.data?.data]);

    return {
        data: events,
        isLoading: query.isPending,
        isFetching: query.isFetching,
        isFetched: query.isFetched,
        error: query.error,
        refetch: query.refetch,
    };
};
